import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertOutlineIcon } from '@navi/web-ui/lib/icons';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { toast } from '@primitives/Toast';
import { hasNotCompletedAction } from 'src/components/Notifications/utils';
import Overlay from 'src/components/Overlay/Overlay';
import TopNavBar from 'src/components/TopNavBar/TopNavBar';
import {
  fullMonthNames,
  WAIT_BEFORE_LONGHORN_PERMANENT_BLOCK,
  WAIT_INTERVAL_BEFORE_COUNTDOWN
} from 'src/constants/Common.constants';
import { Roles, WA_PLUGIN_VERSION } from 'src/pages/auth/constants/AuthConstants';
import {
  ActivityStatus,
  CosmosSyncBlockStatus,
  CHECK_LOGIN,
  CHECK_LOGIN_STATUS,
  setCosmosSyncBlockStatus,
  setBreakTimeStarted,
  setIsCosmosSnapshotDisabled,
  setIsLonghornThemeEnabled,
  setLastSyncTimestamp,
  SlashCallStatus
} from 'src/reducers/commonSlice';
import {
  CLICKSTREAM_EVENT_NAMES,
  HRC_CLICKSTREAM,
  HRC_EVENTS
} from 'src/service/clickStream.constant';
import { addClickstreamEvent } from 'src/service/clickStreamEventService';
import { getServerTime } from 'src/utils/DateHelper';
import { isFunction, isJson } from 'src/utils/commonUtils';
import Loader from '../components/Loader/Loader';
import FcmNotification from '../components/Notifications/FcmNotification';
import {
  fetchNotifications,
  getStickyNotification,
  markNotificationAsRead,
  onPaymentDoneEventReceived
} from '../components/Notifications/NotificationsActions';
import Leaderboard from '../components/leaderboard/Leaderboard';
import { messaging } from '../firebase/firebase';
import {
  fetchCurrentDate,
  getAgencyRanking,
  getCallingWindow,
  getEncryptedSlashCrmId,
  getLeaderboardDetails,
  getPercentCompleted,
  refreshFirebaseTokenAndSignIn,
  registerFCMToken,
  verifyUserSession
} from '../pages/auth/AuthActions';
import { RootState } from '../store';
import APP_ROUTES, { APP_ROUTES_PATHS_WITH_PATH_PARAM } from './Routes';
import { ExtensionHandler } from 'src/utils/extension.utils';
import useFirestoreUpdates from '../firebase/useFirestoreUpdates';
import { setIsAgentOnline } from 'src/reducers/whatsappChatbotSlice';
import { setIsAgentOnline as setIsAgentOnlineLonghorn } from 'src/reducers/commonSlice';
import { AGENT_STATUS, HRC_CHAT_AGENT_ONLINE_STATUS } from 'src/pages/WhatsappChatbot/types';
import { NotificationsData, PaymentDoneData } from 'src/components/Notifications/interfaces';
import {
  NotificationTypes,
  PaymentSuccessTemplateTypes,
  WidgetStatuses
} from 'src/components/Notifications/constants';
import { GlobalPlayer } from '../components/GlobalPlayer/Player';
import { setLonghornSessionVerifier } from '../constants/Global';
import PaymentSuccessModal from '../components/PaymentSuccessModal/PaymentSuccessModal';
import isLitmusExperimentEnabled from '@cp/src/utils/isLitmusExperimentEnabled';
import { LITMUS_EXPERIMENT_NAMES } from '../constants/litmusExperimentNames';
import { LOCAL_STORAGE_KEYS } from '../constants/StorageKeys';
import { LocalStorage } from '../utils/StorageUtils';
import UpdateExtensionNudge from '@cp/components/UpdateExtensionNudge';
import BreakTime from '@cp/components/BreakTime/BreakTime';
import { ameyoDowntimeEventProcessor } from '@cp/utils/fcmProcessors';
import downloadAssetsFromFirestore from '../pages/LonghornTheme';
import LonghornBlock from '../pages/LonghornBlock';
import LonghornWarning from '../pages/LonghornBlock/LonghornWarning';
import { enableLonghornThemForPages, HideTopNavBarRoutes, parseSubscriptionTopic } from './utils';
import {
  bc,
  AUDIO_PLAYER_TOP_OFFSET_WITH_BANNER,
  AUDIO_PLAYER_TOP_OFFSET_WITHOUT_BANNER,
  CALL_AUDIT_MODE_QUERY
} from './const';
import styles from './DefaultLayout.module.scss';
import cx from 'classnames';
import PostOperativeHours from '../pages/PostOperativeHours';
import stateResetterProcessor from '@cp/utils/stateResetterProcessor';
import SnapshotRecorder from '@navi/alfred-session-recorder';
import { noop } from '@utils/common';
import AgencyLeaderboard from '../components/AgencyPerformanceBanner/AgencyLeaderboard';
import logger from '../utils/logger';
import SideNavigationPanel from '../components/sidebar/SideNavigationPanel/index';
import BlockedLonghorn from '../pages/LonghornBlock/BlockedLonghorn';
import { fetchCosmosVersionData } from './actions';
import usePolling from '../hooks/usePolling';
import { getCallAttemptLimitAlert } from '../pages/CaseDetails/components/CustomerSummary/action';

const defaultRoute = {
  key: 'DEFAULT',
  path: '/'
};
const routes: JSX.Element[] = [];
let route: keyof typeof APP_ROUTES;
for (route in APP_ROUTES) {
  routes.push(
    <Route
      key={APP_ROUTES[route].path}
      path={APP_ROUTES[route].path}
      element={APP_ROUTES[route].element}
    />
  );
}
routes.push(
  <Route
    key={defaultRoute.key}
    path={defaultRoute.path}
    element={<Navigate to={APP_ROUTES.CASES.path} />}
  />
);

const DefaultLayout = () => {
  const dispatch = useDispatch();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isDc97Page = APP_ROUTES.HR_DASHBOARD.path === pathname;
  const isLeaveManagementPage = APP_ROUTES.LEAVE_MANAGEMENT.path === pathname;
  const isAgentDashboard = APP_ROUTES.DASHBOARD.path === pathname;
  const isAmeyoGeneratePasswordPage = APP_ROUTES.GENERATE_AMEYO_PASSWORD.path === pathname;
  const user = useSelector((store: RootState) => store?.common?.userData);

  //alfred recording integration
  useEffect(() => {
    let cleanUp: void | undefined | (() => void) = noop;
    if (import.meta.env.DEV) return;
    let recorder = { stopRecording: noop, startSnapshotRecording: noop };
    if (user?.referenceId) {
      recorder = new SnapshotRecorder({
        clientkey: window?.config?.ALFRED_CLIENT_KEY,
        apiUrl: window?.config?.ALFRED_URL,
        projectName: 'longhorn',
        deviceId: user.referenceId,
        ingestEventInterval: 5000,
        metaData: {
          agentId: user.referenceId,
          agentName: user.name,
          agentEmail: user.email,
          deviceId: user.deviceId,
          vertical: 'global'
        }
      });
      cleanUp = recorder?.startSnapshotRecording();
    }

    return () => {
      if (cleanUp && isFunction(cleanUp)) {
        cleanUp();
      }
      recorder.stopRecording();
    };
  }, [user]);
  const isExpanded = useSelector((store: RootState) => store.leaderboard.isExpanded);
  const extensionVersion = useSelector((store: RootState) => store.common.extensionVersion);
  const isLonghornBlocked =
    useSelector((store: RootState) => store.common.cosmosSyncBlockStatus) ===
    CosmosSyncBlockStatus.BLOCKED;

  const agencyCode = useSelector((store: RootState) => store?.common?.userData?.agencyCode);
  const reminderRefId = useSelector((store: RootState) => store?.humanReminder.reminderRefId);
  const referenceId = useSelector((store: RootState) => store?.common?.userData?.referenceId);
  const roles = useSelector((store: RootState) => store?.common?.userData?.roles);
  const isCiuAgent = useSelector((state: RootState) => state.common.isCiuAgent);
  const userImpersonated = useSelector(
    (store: RootState) => store?.common?.userData?.userImpersonated
  );
  const campaignId = useSelector((store: RootState) => store?.common?.userData?.campaignId);
  const fcmToken = useSelector((store: RootState) => store?.common?.fcmToken);
  const withinOperativeHours = useSelector(
    (store: RootState) => store?.common?.userData?.withinOperativeHours
  );

  const isPerformanceDashboardVisible = useSelector(
    (store: RootState) => store?.common?.isPerformanceDashboardVisible
  );

  const notifications = useSelector((store: RootState) => store?.notification.notifications);
  const templatesMap = useSelector((store: RootState) => store?.notification.templatesMap);

  const shouldShowStickyBanner = useSelector(
    (store: RootState) => store?.notification.shouldShowStickyBanner
  );
  const authData = useSelector((store: RootState) => store.common.userData);

  const [showBanner, setShowBanner] = useState(false);
  const [showUpdateExtensionPopper, setShowUpdateExtensionPopper] = useState(false);
  const [notificationData, setNotiFicationData] = useState<Array<NotificationsData>>();
  const isHRCChatUser = roles?.includes(Roles.ROLE_HUMAN_REMINDER_CHAT_AGENT);
  const isDc97User =
    (roles?.includes(Roles.ROLE_HUMAN_REMINDER_AGENT) ||
      roles?.includes(Roles.ROLE_HUMAN_REMINDER_SPADE_CALLING_AGENT)) &&
    !roles.includes(Roles.ROLE_HUMAN_REMINDER_CHAT_AGENT);
  const isTeamLead = useSelector((store: RootState) => store?.common?.isTeamLead);
  const isGlobalAccessUser = roles?.includes(Roles.ROLE_GLOBAL_ACCESS);
  const todaysDate = new Date(getServerTime());
  const currMonth = fullMonthNames[todaysDate?.getMonth() || 0];
  const currYear = todaysDate?.getFullYear();
  const isTeleChatAgent = roles?.includes(Roles.ROLE_TELE_CHAT_AGENT);
  const isNaviFieldAgencyTeamLead = roles?.includes(Roles.ROLE_NAVI_FIELD_AGENCY_TEAM_LEAD);
  const isAgentOnline = useSelector((store: RootState) => store.common.isAgentOnline);
  const isImpersonation = useSelector(
    (state: RootState) => state?.common?.userData?.userImpersonated
  );

  const featureFlags = useSelector((store: RootState) => store?.common?.featureFlags);
  const { cosmosSyncOnLonghornFeatureFlag } = featureFlags || {};
  const isAgentWithInOperativeHours = useSelector(
    (state: RootState) => state?.common?.userData?.withinOperativeHours
  );

  const isCosmosUpdateRequired = useSelector(
    (store: RootState) => store.common.isCosmosUpdateRequired
  );

  const isProcessingFirebaseMessage = roles && !isDc97User && !isCiuAgent;

  useEffect(() => {
    dispatch(verifyUserSession());
    dispatch(getEncryptedSlashCrmId());
    dispatch(fetchCurrentDate());
    dispatch(getCallingWindow());
  }, []);

  usePolling({
    promiseFn: () => dispatch(fetchCosmosVersionData()),
    pollingInterval: 30000
  });

  useEffect(() => {
    if (isPerformanceDashboardVisible) return;
    if (referenceId) {
      dispatch(getLeaderboardDetails(referenceId, currMonth, '', currYear));
      if (!isTeamLead && !isGlobalAccessUser) {
        dispatch(getPercentCompleted());
      }
    }
  }, [referenceId, isPerformanceDashboardVisible, isTeamLead]);

  useEffect(() => {
    if (user?.agencyCode && isTeamLead && isNaviFieldAgencyTeamLead) {
      dispatch(getAgencyRanking(user?.agencyCode));
    }
  }, [isNaviFieldAgencyTeamLead, isTeamLead]);

  useEffect(() => {
    if (fcmToken && !userImpersonated) {
      registerFCMToken(fcmToken);
    }
  }, [fcmToken]);

  useEffect(() => {
    const height = window?.innerHeight;
    const width = window?.innerWidth;

    if (roles?.length && typeof navigator !== undefined) {
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_SCREEN_WIDTH_HEIGHT, {
        height,
        width,
        roles,
        userAgent: navigator?.userAgent
      });
    }
  }, [roles]);

  if (
    localStorage.getItem(CHECK_LOGIN) === CHECK_LOGIN_STATUS.TRUE &&
    referenceId &&
    roles?.includes(Roles.ROLE_HUMAN_REMINDER_AGENT)
  ) {
    localStorage.setItem(CHECK_LOGIN, CHECK_LOGIN_STATUS.FALSE);
    addClickstreamEvent(HRC_CLICKSTREAM[HRC_EVENTS.HRC_LOGIN_SUCCESSFUL], {
      agentRefId: referenceId,
      reminderRefId,
      campaignId,
      ameyoStatus: ExtensionHandler.currentAmeyoStatus()
    });
  }

  useEffect(() => {
    if (isHRCChatUser) {
      navigate(APP_ROUTES.WHATSAPP_CHATBOT.path);
      return;
    } else if (roles?.length && isDc97User) {
      navigate(APP_ROUTES.HR_DASHBOARD.path);
      return;
    }
  }, [roles]);

  if (isProcessingFirebaseMessage) {
    messaging.onMessage(response => {
      // Track FCM notification received
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Notifications_NOTIFICATION_RECEIVED, {
        source: 'FCM',
        notificationType: 'REAL_TIME',
        timestamp: new Date().toISOString(),
        payload: JSON.stringify(response?.data?.payload || {})
      });
      processFirebaseMessage(response?.data?.payload);
    });
    bc.onmessage = e => {
      // Track broadcast channel notification (still FCM origin)
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Notifications_NOTIFICATION_RECEIVED, {
        source: 'FCM_BROADCAST',
        notificationType: 'BROADCAST_CHANNEL',
        timestamp: new Date().toISOString(),
        payload: JSON.stringify(e?.data || {})
      });
      processFirebaseMessage(e?.data);
    };
  }

  const processFirebaseMessage = (payload: any) => {
    if (payload) {
      const notificationData = isJson(payload);
      if (notificationData) {
        dispatch(fetchNotifications());
        const { flowType, notificationTemplate, params, id } = notificationData;
        if (
          LocalStorage.getItem(LOCAL_STORAGE_KEYS.ENABLE_PAYMENT_SUCCESS_MODAL) &&
          PaymentSuccessTemplateTypes.includes(notificationTemplate?.templateName)
        ) {
          const payload: PaymentDoneData = {
            customerName: params?.customerName || '',
            amount: params?.amount ? Number(params.amount) : 0,
            id: id
          };
          dispatch(onPaymentDoneEventReceived(payload));
          return;
        }
        if (notificationTemplate.templateName === 'LONGHORN_STATE_RESETTER_NOTIFICATION') {
          stateResetterProcessor(notificationData, authData?.referenceId || '');
          return;
        }

        if (
          notificationTemplate.templateName === 'AMEYO_CAMPAIGN_DOWN' ||
          notificationTemplate.templateName === 'AMEYO_CAMPAIGN_UP' ||
          notificationTemplate.templateName === 'AMEYO_LOW_CALLS' ||
          notificationTemplate.templateName === 'AMEYO_NORMAL_CALLS'
        ) {
          ameyoDowntimeEventProcessor(notificationData, dispatch);
          return;
        }
        if (notificationTemplate.templateName === 'SUBSCRIPTION_BASED_NOTIFICATION_NUDGE') {
          const subscriptionTopic = params?.subscriptionTopic || '';
          const { businessVertical, caseId } = parseSubscriptionTopic(subscriptionTopic);

          if (caseId && businessVertical) {
            dispatch(getCallAttemptLimitAlert(businessVertical, caseId));
            return;
          }
        }

        if (flowType === NotificationTypes.LONGHORN_STICKY_BANNER) {
          dispatch(getStickyNotification());
          return;
        }
        if (flowType !== NotificationTypes.LONGHORN_POPUP) {
          toast(<FcmNotification notification={notificationData} />, {
            className: styles.toast
          });
        }
      }
    }
  };

  useEffect(() => {
    if (!authData?.referenceId) return;
    isLitmusExperimentEnabled(LITMUS_EXPERIMENT_NAMES.COLLECTION_ENABLE_PAYMENT_SUCCESS_MODAL, {
      'x-customer-id': authData?.referenceId
    }).then(res => {
      LocalStorage.setItem(LOCAL_STORAGE_KEYS.ENABLE_PAYMENT_SUCCESS_MODAL, res?.result);
    });

    isLitmusExperimentEnabled(LITMUS_EXPERIMENT_NAMES.COLLECTION_LONGHORN_THEME_ENABLE, {
      'x-customer-id': authData?.referenceId
    }).then(res => {
      const isLonghornThemeEnabledForPathname = enableLonghornThemForPages.includes(pathname);
      if (isLonghornThemeEnabledForPathname && res?.result) {
        dispatch(setIsLonghornThemeEnabled(true));
        downloadAssetsFromFirestore();
      } else {
        dispatch(setIsLonghornThemeEnabled(false));
      }
    });
  }, [authData?.referenceId, pathname]);

  useEffect(() => {
    if (authData?.userActivityStatus?.activityStatus === ActivityStatus.ON_BREAK) {
      dispatch(setIsAgentOnlineLonghorn(false));
      dispatch(setBreakTimeStarted(authData?.userActivityStatus?.breakStartTime));
    } else {
      dispatch(setIsAgentOnlineLonghorn(true));
    }
  }, [authData?.userActivityStatus]);

  const isDashBoard =
    pathname === APP_ROUTES.DASHBOARD.path || pathname === APP_ROUTES.LIVE_LOCATION_TRACKER.path;
  const shouldHideLeaderboardForChatUser =
    pathname.includes(APP_ROUTES.WHATSAPP_CHATBOT.path) && isTeleChatAgent;

  const shouldHideCsaLeaderBoard = [
    APP_ROUTES_PATHS_WITH_PATH_PARAM.CSA_COORDINATION.tasks_for_me,
    APP_ROUTES_PATHS_WITH_PATH_PARAM.CSA_COORDINATION.request_field_support,
    APP_ROUTES_PATHS_WITH_PATH_PARAM.CSA_DASHBOARD.csa,
    APP_ROUTES_PATHS_WITH_PATH_PARAM.CSA_DASHBOARD.dme
  ].some(path => pathname.includes(path));

  const isCallAuditMode = search.includes(CALL_AUDIT_MODE_QUERY);

  const hideTopNavBarRoutes =
    HideTopNavBarRoutes.includes(pathname) ||
    (!isAgentOnline && !userImpersonated) ||
    (isLonghornBlocked && !userImpersonated) ||
    (!isAgentWithInOperativeHours && !userImpersonated) ||
    pathname.includes(APP_ROUTES.WHATSAPP_CHATBOT.path) ||
    isCallAuditMode;

  const unsubscribeFirestoreUpdates = useFirestoreUpdates();

  useEffect(() => {
    dispatch(
      setIsAgentOnline(localStorage.getItem(HRC_CHAT_AGENT_ONLINE_STATUS) === AGENT_STATUS.ONLINE)
    );
    setLonghornSessionVerifier();
    return () => {
      isFunction(unsubscribeFirestoreUpdates) && unsubscribeFirestoreUpdates();
    };
  }, []);

  useEffect(() => {
    const popupNotifications = notifications?.filter(
      notification => notification.flowType === NotificationTypes.LONGHORN_POPUP
    );
    if (!popupNotifications.length) return;
    const notificationData = popupNotifications[0];
    if (!hasNotCompletedAction(notificationData)) return;
    setNotiFicationData(popupNotifications);
    setShowBanner(true);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_APP_OPEN_NUDGE_SHOWN, {
      agentRefrenceId: referenceId
    });
  }, [notifications]);

  const handleCloseBanner = async () => {
    if (!notificationData?.length) return;
    const apiPromises = notificationData.map(notification => {
      const actionCompleted = !hasNotCompletedAction(notification);
      if (actionCompleted) return Promise.resolve();
      const widget = templatesMap[notification?.body?.templateId]?.widgets?.[0];
      const payload = {
        id: notification.id,
        action: {
          widgetId: widget.widgetId,
          widgetStatus: WidgetStatuses.CLICKED,
          timestamp: getServerTime(),
          actionBy: ''
        }
      };
      return markNotificationAsRead(payload);
    });
    const result = await Promise.all(apiPromises);
    dispatch(fetchNotifications());
    setShowBanner(false);
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_APP_OPEN_NUDGE_CLOSED, {
      agentRefrenceId: referenceId
    });
  };

  useEffect(() => {
    if (extensionVersion) {
      const currentExtensionVersion = LocalStorage.getItem(WA_PLUGIN_VERSION);
      if (currentExtensionVersion !== extensionVersion && currentExtensionVersion) {
        setShowUpdateExtensionPopper(true);
      } else {
        setShowUpdateExtensionPopper(false);
      }
    }
  }, [extensionVersion]);

  const shouldShowScroll = isLeaveManagementPage;

  const lastSyncTimestamp = useSelector((state: RootState) => state.common?.lastSyncTimestamp);
  const cosmosSyncBlockStatus = useSelector(
    (store: RootState) => store.common.cosmosSyncBlockStatus
  );
  const serverDate = useSelector((state: RootState) => state?.common?.serverDate);

  const isPermissionSynced = useSelector(
    (state: RootState) => state?.common?.lastSyncPermissionStatus
  );

  useEffect(() => {
    if (!lastSyncTimestamp || !cosmosSyncOnLonghornFeatureFlag) {
      return;
    }
    if (!isPermissionSynced) {
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.BLOCKED));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_SYNC_BLOCK_STATUS_CHANGED, {
        status: CosmosSyncBlockStatus.BLOCKED
      });
      return;
    }
    const timeMinutesBehindWaitTimePermanentBlock =
      new Date(serverDate)?.getTime() - WAIT_BEFORE_LONGHORN_PERMANENT_BLOCK;
    if (timeMinutesBehindWaitTimePermanentBlock > lastSyncTimestamp) {
      logger('Permanent block');
      navigate('/');
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.BLOCKED));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_SYNC_BLOCK_STATUS_CHANGED, {
        status: CosmosSyncBlockStatus.BLOCKED
      });
      ExtensionHandler.setAvailability(false);
      ExtensionHandler.blockSlash();
      return;
    } else {
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.SYNCED));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_SYNC_BLOCK_STATUS_CHANGED, {
        status: CosmosSyncBlockStatus.SYNCED
      });
      ExtensionHandler.unblockSlash();
    }

    const currentTime = Date.now();
    const elapsedTime = currentTime - lastSyncTimestamp;
    const remainingTime = WAIT_INTERVAL_BEFORE_COUNTDOWN - elapsedTime;

    const timer = setTimeout(() => {
      logger('inside the timer countdown');
      dispatch(setCosmosSyncBlockStatus(CosmosSyncBlockStatus.WARNED));
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_COSMOS_SYNC_BLOCK_STATUS_CHANGED, {
        status: CosmosSyncBlockStatus.WARNED
      });
    }, remainingTime);

    return () => {
      logger('Cleared the timeout!');
      clearTimeout(timer);
    };
  }, [lastSyncTimestamp, isPermissionSynced]);

  useEffect(() => {
    logger('Firebase token refreshed and signin');
    dispatch(refreshFirebaseTokenAndSignIn());
  }, [pathname]);

  const slashCallStatus = useSelector(
    (store: RootState) => store.common.slashCallStatusFromExtension
  );
  const isSlashCallActive = slashCallStatus === SlashCallStatus.ANSWERED;

  const routesRenderer = () => {
    if (agencyCode) {
      if (isImpersonation) {
        return <Routes>{routes}</Routes>;
      } else if (!withinOperativeHours) {
        return <PostOperativeHours />;
      } else if (
        cosmosSyncOnLonghornFeatureFlag &&
        cosmosSyncBlockStatus === CosmosSyncBlockStatus.BLOCKED &&
        !isSlashCallActive
      ) {
        return <LonghornBlock />;
      } else if (!isAgentOnline) {
        return <BreakTime />;
      } else {
        return <Routes>{routes}</Routes>;
      }
    }
  };

  if (isCosmosUpdateRequired && cosmosSyncOnLonghornFeatureFlag) {
    return <BlockedLonghorn />;
  }

  return (
    <div className={styles.pageLayout}>
      {showUpdateExtensionPopper && <UpdateExtensionNudge />}
      <div
        className={styles.audioPlayer}
        style={{
          top: shouldShowStickyBanner
            ? AUDIO_PLAYER_TOP_OFFSET_WITH_BANNER
            : AUDIO_PLAYER_TOP_OFFSET_WITHOUT_BANNER
        }}
      >
        <GlobalPlayer />
      </div>
      {shouldShowStickyBanner ? (
        <div
          className={styles.cosmosNudgeBanner}
          style={{
            zIndex: `var(--z-index-default-layout-open-cosmos-alert${isExpanded ? '-active' : ''})`
          }}
        >
          <Typography as="span" variant="p3">
            Please <strong>Open Cosmos App</strong> to avoid this alert!
          </Typography>
        </div>
      ) : null}
      {roles ? (
        <>
          {!hideTopNavBarRoutes ? (
            <TopNavBar
              isDc97User={isDc97User}
              isAgentDashboard={isAgentDashboard}
              isAmeyoGeneratePasswordPage={isAmeyoGeneratePasswordPage}
            />
          ) : null}
          <SideNavigationPanel />
        </>
      ) : null}
      <React.Suspense fallback={<Loader show={true} />}>
        {!isDc97Page &&
        !isDashBoard &&
        !isTeamLead &&
        !shouldHideLeaderboardForChatUser &&
        !shouldHideCsaLeaderBoard &&
        (isAgentOnline || isImpersonation) &&
        (isAgentWithInOperativeHours || isImpersonation) &&
        (!isLonghornBlocked || isImpersonation) ? (
          <Leaderboard stickyBanner={shouldShowStickyBanner} />
        ) : null}
        {isTeamLead || isNaviFieldAgencyTeamLead ? <AgencyLeaderboard /> : null}
        <div
          className={cx(styles.mainContent, {
            [styles.noScroll]: shouldShowScroll,
            [styles.mainContentWithTopBar]: !hideTopNavBarRoutes,
            [styles.mainContentWithStickyBanner]: shouldShowStickyBanner
          })}
        >
          {routesRenderer()}
        </div>
      </React.Suspense>
      <Overlay visible={showBanner && !userImpersonated}>
        <div className={styles.popupBanner}>
          <div className={styles.header}>
            <AlertOutlineIcon color="red" size={'md'} />
            <Typography variant="h3">Attention! Open Cosmos</Typography>
          </div>
          <Typography className={styles.content} variant="p3">
            {' '}
            Please <strong>Open Navi Cosmos app</strong> in order to avoid further alerts
          </Typography>
          <Button onClick={handleCloseBanner} className={styles.button} variant="primary">
            Okay, Got it!
          </Button>
        </div>
      </Overlay>

      {cosmosSyncBlockStatus === CosmosSyncBlockStatus.WARNED && cosmosSyncOnLonghornFeatureFlag ? (
        <LonghornWarning />
      ) : null}

      {LocalStorage.getItem(LOCAL_STORAGE_KEYS.ENABLE_PAYMENT_SUCCESS_MODAL) && (
        <PaymentSuccessModal />
      )}
    </div>
  );
};

export default DefaultLayout;
